import React, { useState } from 'react';
import Table from '@mui/material/Table';
import { useListContext } from '../../ListContext';
import TableHead, { TableHeadProps } from './TableHead';
import TableBody, { TableBodyProps } from './TableBody';
import { TableBodyWithGroupingProps } from './TableBodyWithGrouping';
import { UiThemeOverrider, useUiTheme } from 'framework/uiTheme';
import ListFieldLayout from '../../../ui/layout/ListFieldLayout/ListFieldLayout';
import ListLoadingDecorator from '../ListLoadingDecorator';
import TextField from '../../../ui/field/text/TextField';
import IconButton from '@mui/material/IconButton';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import Alert from '../../../ui/mui-builder/Alert/Alert';
import Box from '@mui/material/Box';
import { listGeneralSharedSx, listGeneralTableSx, tableBaseSx } from '../dataGridSx';

interface DataGridLayoutProps extends TableHeadProps, TableBodyProps {
  isSmallTable?: boolean;
  tableWrapperClassName?: string;
  isSmallLoader?: boolean;
  rowKeySource?: string;
  tableHeadComponent?: React.FC<TableHeadProps>;
  nonExpandedHeader?: string | JSX.Element | (() => string | JSX.Element) | null | undefined;
  tableBodyComponent?: React.FC<TableBodyProps | TableBodyWithGroupingProps>;
  emptyListMessageComponent?: React.ReactNode;
  customEmptyMessage?: string;
}

const CollapsibleDataGridLayout = ({
  children,
  buttons,
  rowProps = {},
  isSmallTable = false,
  isSmallLoader = false,
  tableWrapperClassName = '',
  rowKeySource,
  tableHeadComponent: TableHeadComponent = TableHead,
  nonExpandedHeader,
  tableBodyComponent: TableBodyComponent = TableBody,
  tableRowComponent,
  emptyListMessageComponent = '',
  customEmptyMessage = '',
}: DataGridLayoutProps) => {
  const { data: { items = [] } = {}, loading } = useListContext();
  const [isExpanded, setIsExpanded] = useState(false);
  const { Alert: ThemedAlert = Alert } = useUiTheme();

  const emptyListComponent = () => {
    if (emptyListMessageComponent) {
      return emptyListMessageComponent;
    }

    return (
      <ThemedAlert color="info" sx={{ mt: 1.5, '.MuiAlert-message': { fontSize: '12px' } }}>
        {customEmptyMessage || 'No records found'}
      </ThemedAlert>
    );
  };

  const expandField = (
    <TextField
      width={20}
      value=" "
      label={
        <IconButton
          size="small"
          onClick={() => setIsExpanded(prevState => !prevState)}
          sx={{
            padding: 0,
            transition: 'transform 0.3s ease',
            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          }}
        >
          <KeyboardArrowDownIcon sx={{ fontSize: '20px' }} />
        </IconButton>
      }
    />
  );

  return (
    <ListLoadingDecorator items={items} loading={loading} isSmallLoader={isSmallLoader}>
      <UiThemeOverrider
        overriddenLayouts={{
          BaseFieldLayout: ListFieldLayout,
        }}
      >
        <Box
          className={isSmallTable ? 'list-general list-general--sm' : undefined}
          sx={{
            display: 'block',
            width: '100%',
            overflowX: 'auto',
            WebkitOverflowScrolling: 'touch',
            ...(isSmallTable ? listGeneralSharedSx : {}),
          }}
        >
          <Box className={tableWrapperClassName || undefined}>
            <Table
              size={isSmallTable ? 'small' : 'medium'}
              className={!isSmallTable ? 'list-general' : undefined}
              sx={isSmallTable ? tableBaseSx : { ...tableBaseSx, ...listGeneralTableSx }}
            >
              <TableHeadComponent isShowHeader buttons={buttons}>
                {expandField}
                {isExpanded && items.length ? children : <TextField value=" " label={nonExpandedHeader} />}
              </TableHeadComponent>
              {isExpanded && items.length > 0 && (
                <TableBodyComponent
                  rowProps={rowProps}
                  buttons={buttons}
                  rowKeySource={rowKeySource}
                  tableRowComponent={tableRowComponent}
                >
                  {expandField}
                  {children}
                </TableBodyComponent>
              )}
            </Table>
            {isExpanded && !items.length && emptyListComponent()}
          </Box>
        </Box>
      </UiThemeOverrider>
    </ListLoadingDecorator>
  );
};

export default CollapsibleDataGridLayout;
